import { useMutation } from '@apollo/client';
import React, { useState } from 'react';

import { SettingsImageUpload } from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  useTenantLogo,
  useTenantName,
  useUploadGenericFile,
} from '@eluve/smart-blocks';

import { updateTenantDetailsMutation } from './operations';

export const TenantLogoSettings: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { tenantLogoUrl } = useTenantLogo(tenantId);
  const tenantName = useTenantName(tenantId);
  const [isUploading, setIsUploading] = useState(false);
  const { uploadGenericFileToGCS } = useUploadGenericFile(tenantId);
  const toast = useToast();
  const { t } = useTranslation();

  const [updateTenantDetails] = useMutation(updateTenantDetailsMutation, {
    onError: () => {
      toast.error(t('tenantAdmin.general.logoUpdateFailed'));
    },
  });

  const handleFileSelect = async (file: File) => {
    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();

      if (!fileExt) {
        toast.error(t('tenantAdmin.common.invalidFileExtension'));
        return;
      }

      const result = await uploadGenericFileToGCS({
        file,
        mimeType: 'image/*',
        fileExt,
      });

      if (result.url) {
        await updateTenantDetails({
          variables: {
            tenantId,
            logoUrl: result.url,
            name: tenantName,
          },
        });
      }
    } catch {
      toast.error(t('tenantAdmin.general.logoUpdateFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsUploading(true);
      await updateTenantDetails({
        variables: {
          tenantId,
          logoUrl: '',
          name: tenantName,
        },
      });
    } catch {
      toast.error(t('tenantAdmin.general.logoDeleteFailed'));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <SettingsImageUpload
      src={tenantLogoUrl}
      alt={t('tenantAdmin.general.organizationLogoAlt')}
      disabled={isUploading}
      onSelect={handleFileSelect}
      onDelete={handleDelete}
    />
  );
};
